import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { OI_URL } from "../config/config";
import DefaultPageSize from "./Table/DefaultPageSize";
import PermissionsRequired from "./PermissionsRequired";

const DataFetchHookOI = (initialurl, requestMethod = "POST", accessToken = null, defaultRequestData = { page: 1, size: 20, order: "DESC", sortField: "dateCreated" }, maxRange = 3) => {
    const [data, setData] = useState({ datalist: [] });
    const [totalResult, setTotalResult] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [isError, setIsError] = useState(false);
    const [url, setUrl] = useState(OI_URL + initialurl);
    
    // Ensure we start with numeric values for pagination
    const initialPageSize = parseInt(DefaultPageSize()) || 20;
    const initialRequestData = {
        ...defaultRequestData,
        page: parseInt(defaultRequestData.page) || 1,
        size: initialPageSize,
        associatedMerchantIds: Cookies.get("oiAssociation") ? Cookies.get("oiAssociation").split(",").filter(item => item.trim() !== "") : []
    };
    
    const [requestData, setRequestData] = useState(initialRequestData);
    const [method, setMethod] = useState(requestMethod);

    // FIX: Pass through dateFrom and dateTo as they are, without trying to transform them
    // This allows the CartList component to have full control over date formatting
    const getStartDate = () => requestData.dateFrom || null;
    const getEndDate = () => requestData.dateTo || null;
   
    // Ensure pagination parameters are always numeric
    const getPage = () => parseInt(requestData?.page) || 1;
    const getSize = () => parseInt(requestData?.size) || initialPageSize;
    
    // Ensure sort parameters have valid values
    const getOrder = () => 
        requestData?.order === "ASC" ? "ASC" : "DESC";
    
    const getSortField = () => 
        requestData?.sortField || "dateCreated";

    // Custom requestData setter that ensures page and size are numbers
    const safeSetRequestData = (newData) => {
        // If newData is a function, call it with current data and normalize the result
        if (typeof newData === 'function') {
            setRequestData(prevData => {
                const updatedData = newData(prevData);
                return {
                    ...updatedData,
                    page: parseInt(updatedData.page) || 1,
                    size: parseInt(updatedData.size) || initialPageSize
                };
            });
        } else {
            // Otherwise, just normalize the new data
            setRequestData({
                ...newData,
                page: parseInt(newData.page) || 1,
                size: parseInt(newData.size) || initialPageSize
            });
        }
    };

    useEffect(() => {
        const fetchdata = async () => {
            setIsLoading(true);
            setIsError(false);
            
            try {
                const newtoken = Cookies.get("txfind_token");
                if (!newtoken) {
                    console.error("Error: Token is undefined");
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }
                
                let options = { headers: { Authorization: "Bearer " + newtoken } };
        
                // Ensure we always send proper pagination parameters
                const pageParam = getPage();
                const sizeParam = getSize();
                
                // NEW: Check if user is SysAdmin or has merchant associations
                const isSysAdmin = PermissionsRequired("SYSADMIN");
                const oiAssociation = Cookies.get("oiAssociation") ? Cookies.get("oiAssociation").split(",").filter(item => item.trim() !== "") : [];
                
                // NEW: Create parameter object with merchant associations if needed
                let param = { 
                    ...requestData, 
                    dateFrom: getStartDate(), 
                    dateTo: getEndDate(), 
                    page: pageParam, 
                    size: sizeParam, 
                    order: getOrder(),
                    sortField: getSortField()
                };
                
                // NEW: Add associatedMerchantIds if user is not SysAdmin and they aren't already set
                if (!isSysAdmin && oiAssociation.length > 0 && 
                    (!param.associatedMerchantIds || param.associatedMerchantIds.length === 0)) {
                    param.associatedMerchantIds = oiAssociation;
                    console.log("Auto-applying merchant association filter:", oiAssociation);
                }
                                
                let result;                
                if (method === "POST") {
                    result = await axios.post(url, param, options);
                } else {
                    // For GET requests, convert params to query string
                    if (method === "GET") {
                        const queryParams = new URLSearchParams();
                        for (const key in param) {
                            if (Array.isArray(param[key])) {
                                // Handle arrays by joining with commas
                                if (param[key].length > 0) {
                                    queryParams.append(key, param[key].join(','));
                                }
                            } else if (param[key] !== null && param[key] !== undefined) {
                                queryParams.append(key, param[key]);
                            }
                        }
                        const queryString = queryParams.toString();
                        const separator = url.includes('?') ? '&' : '?';
                        const urlWithParams = queryString ? `${url}${separator}${queryString}` : url;
                        result = await axios.get(urlWithParams, options);
                    } else {
                        result = await axios.get(url, options);
                    }
                }
                
                if (result.data) {
                    if (result.data.list) {
                        setData({ datalist: result.data.list });
                      } else if (result.data.datalist) {
                        setData({ datalist: result.data.datalist });
                      }
                    if (result.data.total >= 0) {
                        setTotalResult(parseInt(result.data.total));
                    }
                    if (result.data.msg) {
                        setErrorMsg(result.data);
                        setIsError(true);
                    }
                } else {
                    console.error("DataFetchHookOI - No data received from API");
                    setIsError(true);
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setIsError(true);
                setErrorMsg(err.message);
            }
            
            setIsLoading(false);
        };
        
        fetchdata();
    }, [url, requestData, method]); // eslint-disable-line react-hooks/exhaustive-deps

    return [{ data, totalResult, requestData, isLoading, isError, errorMsg }, setUrl, safeSetRequestData, setMethod];
};

export default DataFetchHookOI;